// TODO: split up into separate files for combos/suits

import mapValues from "lodash/mapValues";
import difference from "lodash/difference";
import intersection from "lodash/intersection";
import { CARD_VALUES, CARD_SUITS } from "../constants";
import { arrayStringContains, arrayStringEquals, isArrayContainedIn } from "./collections";

const cardRegexpString = `[${CARD_VALUES.join("")}][${CARD_SUITS.join("")}]`;

export const normalizeCardString = card => {
  return `${card.charAt(0).toUpperCase()}${card.charAt(1).toLowerCase()}`;
};
export const stringToCards = string => {
  const cards = string.match(new RegExp(cardRegexpString, "gi")) || [];
  const normalizedCards = cards.map(card => normalizeCardString(card));
  const uniqueCards = [...new Set(normalizedCards)];
  return uniqueCards;
};
export const cardsToString = cards => cards.map(card => normalizeCardString(card)).join(", ");
export const additionalCharactersAfterStringToCards = string => {
  const cards = stringToCards(string);
  for (let i = 1; i < string.length; ++i) {
    const shorterString = string.substr(0, string.length - i);
    const extraCharacters = string.substr(string.length - i);
    const cardsOfShorterString = stringToCards(shorterString);
    if (cardsOfShorterString.length < cards.length) {
      return string.substr(string.length - i + 1);
    }
  }
  return string;
};

export const cardToSuit = card => card.substr(1, 1);
export const cardsToSuits = cards => cards.map(card => cardToSuit(card));

// ["Ac", "Ad", "Ah", "As", "Kc", "Kd", ...]
const deck = (() => {
  const result = [];
  for (let value of CARD_VALUES) {
    for (let suit of CARD_SUITS) {
      result.push(`${value}${suit}`);
    }
  }
  return result;
})();

// [["Ac", "Ad"], ["Ac", "Ah"], ...]
export const allCombos = (() => {
  const result = [];
  for (let i = 0; i < deck.length; ++i) {
    for (let j = i + 1; j < deck.length; ++j) {
      result.push([deck[i], deck[j]]);
    }
  }
  return result;
})();
// ["Ac", "8d"] => "Ac8d"
export const comboToComboString = combo => combo.join("");
// "Ac8d" => ["Ac", "8d"]
export const comboStringToCombo = comboString => [
  comboString.substr(0, 2),
  comboString.substr(2, 2),
];
// ["Ac", "8d"] => ["8d", "Ac"]
const reverseComboString = comboString =>
  comboToComboString(comboStringToCombo(comboString).reverse());
// ["AcAd", "AcAh", ...]
export const allComboStrings = allCombos.map(combo => comboToComboString(combo));

// ["AA", "AKs", "AKo", "AQs", ...]
export const allSquareStrings = (() => {
  const result = [];
  for (const [i, valueA] of CARD_VALUES.entries()) {
    for (const [j, valueB] of CARD_VALUES.entries()) {
      if (i < j) result.push(`${CARD_VALUES[i]}${CARD_VALUES[j]}s`);
      else if (i === j) result.push(`${CARD_VALUES[i]}${CARD_VALUES[j]}`);
      else if (i > j) result.push(`${CARD_VALUES[j]}${CARD_VALUES[i]}o`);
    }
  }
  return result;
})();

// {AcAd: "AA", AdAc: "AA", ..., Ac8d: "A8o", ...}
const squareStringsByComboString = (() => {
  const result = {};
  for (let comboString of allComboStrings) {
    let suitedOrUnsuited;
    const [valueA, suitA, valueB, suitB] = comboString.split("");
    if (suitA === suitB) suitedOrUnsuited = "s";
    else suitedOrUnsuited = valueA === valueB ? "" : "o";
    const squareString = `${valueA}${valueB}${suitedOrUnsuited}`;
    result[comboString] = result[reverseComboString(comboString)] = squareString;
  }
  return result;
})();
// "Ac8d" => "A8o"
export const comboStringToSquareString = comboString => squareStringsByComboString[comboString];
// ["Ac", "8d"] => "A8o"
export const comboToSquareString = comboString =>
  squareStringsByComboString[comboToComboString(comboString)];
export const comboStringsToSquareStrings = comboStrings => {
  const squareStrings = comboStrings.map(comboString => comboStringToSquareString(comboString));
  return [...new Set(squareStrings)];
};

// {AA: [["Ac", "Ad"], ["Ac", "Ah"], ...], AKs: [["Ac", "Kc"], ...], ...}
const combosBySquareString = (() => {
  const result = {};
  const getEquivalentCombos = combo =>
    allCombos.filter(_combo => comboToSquareString(_combo) === comboToSquareString(combo));
  for (let squareString of allSquareStrings) {
    const [valueA, valueB, suitedOrUnsuited] = squareString.split("");
    const representative =
      suitedOrUnsuited === "s" ? [`${valueA}s`, `${valueB}s`] : [`${valueA}s`, `${valueB}c`];
    result[squareString] = getEquivalentCombos(representative);
  }
  return result;
})();
// {AA: ["AcAd", "AcAh", ...], AKs: ["AcKc", ...], ...}
const comboStringsBySquareString = mapValues(combosBySquareString, combos =>
  combos.map(comboToComboString)
);
// "AA" => [["Ac", "Ad"], ["Ac", "Ah"], ["Ac", "As"], ["Ad", "Ah"], ["Ad", "As"], ["Ah", "As"]]
const squareStringToCombos = squareString => combosBySquareString[squareString];
// "AA" => ["AcAd", "AcAh", ...]
export const squareStringToComboStrings = squareString => comboStringsBySquareString[squareString];

export const isSquareStringPocketPair = squareString => {
  return squareString.substr(0, 1) === squareString.substr(1, 1);
};

export const isSquareStringSuited = squareString => {
  return squareString.substr(2, 1) === "s";
};

export const isComboStringPocketPair = comboString => {
  return comboString.substr(0, 1) === comboString.substr(2, 1);
};

export const allSuitCombos = (() => {
  const result = [];
  for (const suitA of CARD_SUITS) {
    for (const suitB of CARD_SUITS) {
      result.push([suitA, suitB]);
    }
  }
  return result;
})();

export const allSuitComboStrings = allSuitCombos.map(combo => combo.join(""));

const suitComboStringsByHighCardSuit = {
  h: ["hh", "hd", "hs", "hc"],
  d: ["dd", "ds", "dc", "dh"],
  s: ["ss", "sc", "sh", "sd"],
  c: ["cc", "ch", "cd", "cs"],
};

const pocketPairSuitComboStrings = (() => {
  const result = [];
  for (let i = 0; i < CARD_SUITS.length; ++i) {
    for (let j = i + 1; j < CARD_SUITS.length; ++j) {
      result.push(`${CARD_SUITS[i]}${CARD_SUITS[j]}`);
    }
  }
  return result;
})();

const reverseSuitComboString = suitComboString =>
  suitComboString
    .split("")
    .reverse()
    .join("");

const suitComboStringsByLowCardSuit = mapValues(suitComboStringsByHighCardSuit, suitComboStrings =>
  suitComboStrings.map(reverseSuitComboString)
);

const suitComboStringsByContainedSuit = mapValues(
  suitComboStringsByHighCardSuit,
  (combos, suit) => {
    return [...new Set([...combos, ...suitComboStringsByLowCardSuit[suit]])];
  }
);

export const suitQuickSelectionToSuitComboStrings = selection => {
  let suitComboStrings = [];
  for (let suit of CARD_SUITS) {
    if (!selection[suit]) continue;
    if (selection[suit].any) {
      suitComboStrings = [...suitComboStrings, ...suitComboStringsByContainedSuit[suit]];
    } else if (selection[suit].high) {
      suitComboStrings = [...suitComboStrings, ...suitComboStringsByHighCardSuit[suit]];
    }
  }
  const uniqueSuitComboStrings = [...new Set(suitComboStrings)];
  return uniqueSuitComboStrings;
};

export const suitComboStringsToSuitQuickSelection = suitComboStrings => {
  const quickSelection = {};
  for (let suit of CARD_SUITS) {
    quickSelection[suit] = { any: false, high: false };
    if (isArrayContainedIn(suitComboStringsByContainedSuit[suit], suitComboStrings)) {
      quickSelection[suit].any = true;
    } else if (isArrayContainedIn(suitComboStringsByHighCardSuit[suit], suitComboStrings)) {
      quickSelection[suit].high = true;
    }
  }
  return quickSelection;
};

const isSuitComboStringSuited = suitComboString => {
  return suitComboString.substr(0, 1) === suitComboString.substr(1, 1);
};

const squareStringToPossibleSuitComboStrings = squareString => {
  if (isSquareStringSuited(squareString)) {
    return allSuitComboStrings.filter(suitComboString => isSuitComboStringSuited(suitComboString));
  } else {
    return allSuitComboStrings.filter(suitComboString => !isSuitComboStringSuited(suitComboString));
  }
};

export const squareComboStringsToSuitRules = (squareString, comboStrings) => {
  const isPocketPair = isSquareStringPocketPair(squareString);
  if (isPocketPair) {
    comboStrings = [...comboStrings, ...comboStrings.map(reverseComboString)];
  }
  const possibleSuitComboStrings = squareStringToPossibleSuitComboStrings(squareString);
  const suitComboStrings = comboStrings.map(comboStringToSuitComboString);
  const rules = [];
  let comboStringsCoveredByRules = [];
  if (!suitComboStrings.length) return rules;
  for (const suit of CARD_SUITS) {
    const possibleSuitComboStringsContainingSuit = intersection(
      suitComboStringsByContainedSuit[suit],
      possibleSuitComboStrings
    );
    const possibleSuitComboStringsWithHighSuit = intersection(
      suitComboStringsByHighCardSuit[suit],
      possibleSuitComboStrings
    );
    if (isArrayContainedIn(possibleSuitComboStringsContainingSuit, suitComboStrings)) {
      rules.push({ suit, rule: "any" });
      comboStringsCoveredByRules = [
        ...comboStringsCoveredByRules,
        ...possibleSuitComboStringsContainingSuit,
      ];
    } else if (
      !isPocketPair &&
      isArrayContainedIn(possibleSuitComboStringsWithHighSuit, suitComboStrings)
    ) {
      rules.push({ suit, rule: "high" });
      comboStringsCoveredByRules = [
        ...comboStringsCoveredByRules,
        ...possibleSuitComboStringsWithHighSuit,
      ];
    }
  }
  const uniqueComboStringsCoveredByRules = [...new Set(comboStringsCoveredByRules)];
  // Only return complete rule sets
  if (uniqueComboStringsCoveredByRules.length < comboStrings.length) return [];
  return rules;
};

export const suitToContainingSuitComboStrings = suit => {
  return suitComboStringsByContainedSuit[suit];
};

const comboToSuitComboString = combo => combo.map(card => card.substr(1, 1)).join("");
export const comboStringToSuitComboString = comboString =>
  comboToSuitComboString(comboStringToCombo(comboString));

const filterComboStringsBySuits = (comboStrings, allowedSuitComboStrings) =>
  comboStrings.filter(comboString =>
    allowedSuitComboStrings.includes(comboStringToSuitComboString(comboString))
  );

export const doComboStringsMatchSuitComboStrings = (
  availableComboStrings,
  comboStrings,
  suitComboStrings
) => {
  const availableComboStringsMatchingSuits = filterComboStringsBySuits(
    availableComboStrings,
    suitComboStrings
  );
  const isMatch = arrayStringEquals(availableComboStringsMatchingSuits, comboStrings, true);
  return isMatch;
};

export const invertSuitComboStringSet = suitComboStrings => {
  return difference(allSuitComboStrings, suitComboStrings);
};

export const comboStringsToSuitPartition = (comboStrings, suitComboStrings) => {
  const matchingComboStrings = comboStrings.filter(comboString => {
    const suitComboString = comboStringToSuitComboString(comboString);
    if (suitComboStrings.includes(suitComboString)) return true;
    if (
      isComboStringPocketPair(comboString) &&
      suitComboStrings.includes(reverseSuitComboString(suitComboString))
    )
      return true;
    return false;
  });
  const notMatchingComboStrings = comboStrings.filter(
    comboString => !matchingComboStrings.includes(comboString)
  );
  return { matching: matchingComboStrings, notMatching: notMatchingComboStrings };
};

export const excludeCardsFromComboStrings = (comboStrings, cards) => {
  return comboStrings.filter(comboString => {
    const combo = comboStringToCombo(comboString);
    const normalizedCards = cards.map(card => normalizeCardString(card));
    if (normalizedCards.includes(combo[0])) return false;
    if (normalizedCards.includes(combo[1])) return false;
    return true;
  });
};

export const subrangeByComboStringToComboStringsBySubrange = subrangeByComboString => {
  return Object.entries(subrangeByComboString).reduce((obj, [comboString, subrangeIndex]) => {
    if (obj[subrangeIndex] === undefined) {
      obj[subrangeIndex] = [];
    }
    obj[subrangeIndex].push(comboString);
    return obj;
  }, {});
};

export const boardToGroupedBoard = board => {
  const groups = {};
  if (board.length < 3) return groups;
  groups["flop"] = board.slice(0, 3);
  if (board.length < 4) return groups;
  groups["turn"] = board.slice(3, 4);
  if (board.length < 5) return groups;
  groups["river"] = board.slice(4, 5);
  return groups;
};
